import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Alert,
    IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

const StyledContainer = styled(Container)(({ theme }) => ({
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f2f5',
}));

const StyledPaper = styled(Box)(({ theme }) => ({
    display: 'flex',
    width: '100%',
    maxWidth: 1000,
    height: 600,
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
}));

const IllustrationBox = styled(Box)(({ theme }) => ({
    flex: 1,
    backgroundColor: '#003c50',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
    color: 'white',
}));

const LoginBox = styled(Box)(({ theme }) => ({
    flex: 1,
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(8),
}));

const Login = () => {
    const [companyInfo, setCompanyInfo] = useState(null);
    const [companyLoading, setCompanyLoading] = useState(true);
    const [companyError, setCompanyError] = useState(null);

    useEffect(() => {
        const fetchCompanyInfo = async () => {
            try {
                setCompanyLoading(true);
                setCompanyError(null);
                const response = await fetch(`${process.env.REACT_APP_MOBILE_API_URL}/api/company/${process.env.REACT_APP_API_COMPANY}`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${process.env.REACT_APP_API_AUTH_TOKEN}`
                    }
                });
                const data = await response.json();
                console.log(data);
                if (data.result) {
                    setCompanyInfo(data.result);
                } else {
                    console.log(data);
                    setCompanyError('Failed to fetch company info');
                    
                }
            } catch (error) {
                setCompanyError('Failed to fetch company info');
            } finally {
                setCompanyLoading(false);
            }
        };
        fetchCompanyInfo();
    }, []);
    const [loginType, setLoginType] = useState("id");
    const [identifier, setIdentifier] = useState("");
    const [lastName, setLastName] = useState("");
    const [errors, setErrors] = useState({});
    const [loginError, setLoginError] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_API_BASE_URL;

    const handleLoginTypeChange = (event, newLoginType) => {
        setLoginType(newLoginType);
        setIdentifier("");
        setErrors({});
    };

    const validateFields = () => {
        const newErrors = {};

        // Check if identifier is filled
        if (!identifier) {
            newErrors.identifier = `${loginType === "id" ? "User ID" : "SSN"} is required`;
        } else if (!/^\d+$/.test(identifier)) {
            newErrors.identifier = `${loginType === "id" ? "User ID" : "SSN"} must be numeric`;
        } else if (loginType === "ssn" && identifier.length !== 5) {
            newErrors.identifier = "SSN must be exactly 5 digits";
        }

        // Check if last name is filled
        if (!lastName) {
            newErrors.lastName = "Last Name is required";
        }

        return newErrors;
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        const fieldErrors = validateFields();

        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            return;
        }

        const payload = {
            input_lastname: lastName.toUpperCase(),
            input_ssn: loginType === 'ssn' ? identifier : "0",
            input_id: loginType === 'id' ? identifier : "0",
        };

        try {
            setLoading(true);
            setLoginError(null);

            const response = await fetch(`${apiUrl}/donor/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (data.status_code === 0) {
                navigate("/dashboard", { state: { name: lastName,id:data.result } });
            } else {
                setLoginError(data.message);
            }
        } catch (error) {
            setLoginError("An error occurred. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <StyledContainer maxWidth={false}>
            
            <StyledPaper>
                <IllustrationBox sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%'
                }}>
                    <Box component="img"
                        src="/edonorsoft.png"
                        alt="Edonorsoft Logo"
                        sx={{
                            maxWidth: '60%',
                            height: 'auto',
                            marginBottom: 4
                        }}
                    />
                    <Typography variant="h4" gutterBottom align="center">
                        Booking made
                    </Typography>
                    <Typography variant="h4" gutterBottom align="center">
                        Easy
                    </Typography>
                </IllustrationBox>
                <LoginBox>
                <Box
    sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: 4,
    }}
>
    {companyLoading ? (
        <Typography variant="body2" color="textSecondary">Loading company info...</Typography>
    ) : companyError ? (
        <Alert severity="error">{companyError}</Alert>
    ) : companyInfo && (
        <Box
            sx={{
                background: 'transparent', // Remove card look
                px: 0,
                py: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 0,
                minWidth: 0,
                maxWidth: 340,
            }}
        >
            <img
                src={companyInfo.logo}
                alt={companyInfo.company}
                style={{
                    maxHeight: 70,
                    marginBottom: 4, // Make the space between logo and text smaller
                    borderRadius: 8,
                    background: '#fff',
                    padding: 6,
                    objectFit: 'contain',
                }}
            />
            <Typography
                variant="h5"
                align="center"
                sx={{
                    fontWeight: 700,
                    color: '#003c50',
                    letterSpacing: 0.5,
                    mt: 0,
                    mb: 0
                }}
            >
                {companyInfo.company}
            </Typography>
        </Box>
    )}
</Box>
                    <Typography variant="h6" align="center" gutterBottom>
                        Hello Again!
                    </Typography>
                    <Typography variant="body2" align="center" color="textSecondary" paragraph>
                        Welcome back! Please enter your details to sign in.
                    </Typography>

                    {loginError && (
                        <Alert
                            severity="error"
                            sx={{ mb: 3 }}
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => setLoginError(null)}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                        >
                            {loginError}
                        </Alert>
                    )}

                    <form onSubmit={handleLogin}>
                        <ToggleButtonGroup
                            value={loginType}
                            exclusive
                            style={{ display: 'flex', justifyContent: 'center' }}
                            onChange={handleLoginTypeChange}
                            sx={{ mb: 3 }}
                        >
                            <ToggleButton value="id">Login by ID</ToggleButton>
                            <ToggleButton value="ssn">Login by SSN</ToggleButton>
                        </ToggleButtonGroup>

                        <TextField
                            fullWidth
                            margin="normal"
                            label={loginType === "id" ? "User ID" : "Last 5 digits of SSN"}
                            variant="outlined"
                            value={identifier}
                            onChange={(e) => {
                                setIdentifier(e.target.value);
                                setErrors({ ...errors, identifier: '' });
                            }}
                            error={Boolean(errors.identifier)}
                            helperText={errors.identifier}
                        />

                        <TextField
                            fullWidth
                            margin="normal"
                            label="Last Name"
                            variant="outlined"
                            value={lastName}
                            onChange={(e) => {
                                setLastName(e.target.value);
                                setErrors({ ...errors, lastName: '' });
                            }}
                            error={Boolean(errors.lastName)}
                            helperText={errors.lastName}
                        />

                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            type="submit"
                            size="large"
                            sx={{ mt: 2, mb: 2 }}
                            disabled={loading}
                        >
                            {loading ? "Logging in..." : "Login"}
                        </Button>
                    </form>
                </LoginBox>
            </StyledPaper>
        </StyledContainer>
    );
};

export default Login;
